// ============================================
// ReviewCard — UKA-X01 D6
//
// The card Dr. C interacts with per section.
// One ReviewCard per ClinicalReviewItem.
//
// Design: DM Sans body, no framer-motion, pure CSS transitions.
// Palette: BG #FAFAFA, Card #FFFFFF, Text #18181B, Rose #E11D48
// Min font 13px, WCAG AA contrast, border-radius 4px (Tailwind `rounded`).
// ============================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import type { ClinicalReviewItem, VerdictAction } from '../../data/portal/schema';
import type { ReviewItemBody } from '../../data/portal/contentTypes';
import {
  isFormularySupplementBody,
  isFormularyRxBody,
  isFormularyLongevityBody,
  isBiomarkerRangeBody,
  isEvidenceBriefBody,
  isRangeDecisionBody,
  isGapQuestionBody,
  isMethodologyBody,
  isAttestationBody,
  isProtocolRuleBody,
} from '../../data/portal/contentTypes';
import { useVerdictSubmit, verifyContentHash } from '../hooks/useVerdictSubmit';
import { ApproveAllButton } from './ApproveAllButton';
import type { ApproveAllItem } from './ApproveAllButton';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ReviewCardProps {
  contentId: string;
  item: ClinicalReviewItem;
  tenantId: string;
  onVerdictSubmitted?: (contentId: string, action: VerdictAction) => void;
  /** Title of the next unreviewed item — shown as a button in the success state. */
  nextItemTitle?: string | null;
  /** All items in the same category group — used for ApproveAllButton (formulary_supplement only). */
  groupItems?: ApproveAllItem[];
}

// ── Constants ────────────────────────────────────────────────────────────────

/** Minimum milliseconds before Approve button becomes clickable (accidental tap prevention). */
const APPROVE_LOCKOUT_MS = 2000;

// ── Verdict button config ─────────────────────────────────────────────────────

const VERDICT_BUTTONS: Array<{
  action: VerdictAction;
  label: string;
  bg: string;
  bgHover: string;
  border: string;
  text: string;
  ring: string;
}> = [
  {
    action: 'APPROVE',
    label: 'Approve',
    bg: 'bg-green-600',
    bgHover: 'hover:bg-green-700',
    border: 'border-green-600',
    text: 'text-white',
    ring: 'focus:ring-green-500',
  },
  {
    action: 'APPROVE_WITH_CONDITIONS',
    label: 'Approve With Conditions',
    bg: 'bg-amber-500',
    bgHover: 'hover:bg-amber-600',
    border: 'border-amber-500',
    text: 'text-white',
    ring: 'focus:ring-amber-400',
  },
  {
    action: 'CHANGE_REQUESTED',
    label: 'Request Change',
    bg: 'bg-orange-500',
    bgHover: 'hover:bg-orange-600',
    border: 'border-orange-500',
    text: 'text-white',
    ring: 'focus:ring-orange-400',
  },
  {
    action: 'REJECT',
    label: 'Reject',
    bg: 'bg-rose-600',
    bgHover: 'hover:bg-rose-700',
    border: 'border-rose-600',
    text: 'text-white',
    ring: 'focus:ring-rose-500',
  },
];

// ── ReviewCard ────────────────────────────────────────────────────────────────

export function ReviewCard({
  contentId,
  item,
  tenantId,
  onVerdictSubmitted,
  nextItemTitle,
  groupItems,
}: ReviewCardProps): React.ReactElement {
  const [selectedAction, setSelectedAction] = useState<VerdictAction | null>(null);
  const [comment, setComment] = useState('');
  const [justification, setJustification] = useState('');
  const [approveUnlocked, setApproveUnlocked] = useState(false);
  const [hashMismatch, setHashMismatch] = useState(false);
  const verdictFiredRef = useRef(false);
  const [hashChecked, setHashChecked] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { submitting, error, success, reset, submit } = useVerdictSubmit(
    tenantId,
    contentId,
    item,
  );

  // ── TOCTOU hash check on mount ─────────────────────────────────────────
  useEffect(() => {
    verifyContentHash(item).then(valid => {
      setHashMismatch(!valid);
      setHashChecked(true);
    });
  }, [item]);

  // ── 2-second Approve unlock timer ─────────────────────────────────────
  useEffect(() => {
    setApproveUnlocked(false);
    timerRef.current = setTimeout(() => {
      setApproveUnlocked(true);
    }, APPROVE_LOCKOUT_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [contentId]); // reset timer when card changes

  // ── No auto-advance — user clicks Next button in success state ───────

  // ── Derived validation ─────────────────────────────────────────────────
  const requiresJustification = item.offLabel || item.controversial || item.requiresJustification;
  const commentRequired =
    selectedAction === 'REJECT' || selectedAction === 'CHANGE_REQUESTED';

  const canSubmit =
    selectedAction !== null &&
    !submitting &&
    !hashMismatch &&
    !(selectedAction === 'APPROVE' && !approveUnlocked) &&
    !(requiresJustification && justification.trim().length === 0) &&
    !(commentRequired && comment.trim().length < 10);

  // ── Handlers ───────────────────────────────────────────────────────────
  const handleActionSelect = useCallback((action: VerdictAction) => {
    setSelectedAction(action);
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!selectedAction || !canSubmit) return;
      await submit({
        action: selectedAction,
        comment: comment.trim() || null,
        clinicalJustification: justification.trim() || null,
      });
    },
    [selectedAction, comment, justification, canSubmit, submit],
  );

  const handleReset = useCallback(() => {
    setSelectedAction(null);
    setComment('');
    setJustification('');
    reset();
  }, [reset]);

  // ── Tier label ─────────────────────────────────────────────────────────
  const tierLabel = getTierLabel(item.tier);

  // ── Content type display label ─────────────────────────────────────────
  const typeLabel = getTypeLabel(item.type);

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────

  if (success) {
    return (
      <div className="bg-white rounded border border-green-200 p-6 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
            <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-[#18181B]">
              Verdict recorded: {getActionLabel(selectedAction!)}
            </p>
            <p className="text-sm text-zinc-500 mt-0.5">{item.title}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 mt-4">
          <button
            onClick={handleReset}
            className="text-sm text-zinc-400 hover:text-zinc-600 underline underline-offset-2 transition-colors"
          >
            Review again
          </button>
          {nextItemTitle && (
            <button
              onClick={() => {
                if (!verdictFiredRef.current) {
                  verdictFiredRef.current = true;
                  onVerdictSubmitted?.(contentId, selectedAction!);
                }
              }}
              className="ml-auto flex items-center gap-2 px-4 py-2 bg-[#18181B] text-white text-sm font-medium rounded hover:bg-zinc-700 transition-colors"
            >
              Next: <span className="truncate max-w-[200px]">{nextItemTitle}</span>
              <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white rounded border border-zinc-200 shadow-sm overflow-hidden"
      aria-label={`Review card: ${item.title}`}
    >
      {/* ── Hash mismatch error banner ──────────────────────────────────── */}
      {hashChecked && hashMismatch && (
        <div className="bg-red-50 border-b border-red-200 px-5 py-3 flex items-start gap-2">
          <svg className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
          <p className="text-sm text-red-700 font-medium">
            Content Modified Since Staging — contact admin. All verdict buttons are disabled.
          </p>
        </div>
      )}

      {/* ── Off-label / controversial warning banner ────────────────────── */}
      {(item.offLabel || item.controversial) && (
        <div className="bg-amber-50 border-b border-amber-200 px-5 py-3 flex items-start gap-2">
          <svg className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
          <div>
            <p className="text-sm font-semibold text-amber-800">
              {item.offLabel && item.controversial
                ? 'Off-Label Use · Clinically Controversial'
                : item.offLabel
                ? 'Off-Label Use'
                : 'Clinically Controversial'}
            </p>
            <p className="text-sm text-amber-700 mt-0.5">
              Clinical justification is required before submission.
            </p>
          </div>
        </div>
      )}

      {/* ── Card header ────────────────────────────────────────────────── */}
      <div className="px-5 pt-5 pb-4 border-b border-zinc-100">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-semibold text-[#18181B] leading-snug">
              {item.title}
            </h2>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              {/* Content type badge */}
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-zinc-100 text-zinc-600 border border-zinc-200">
                {typeLabel}
              </span>
              {/* Tier badge (T1/T2/T3 — formulary items only) */}
              {tierLabel && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                  {tierLabel}
                </span>
              )}
              {/* Version */}
              <span className="text-xs text-zinc-400">v{item.version}</span>
              {/* Status */}
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getStatusClasses(item.status)}`}>
                {item.status.replace(/_/g, ' ')}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Safety check panel (MVP: manual review only) ──────────────── */}
      <div className="px-5 py-3 bg-zinc-50 border-b border-zinc-100">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium bg-zinc-100 text-zinc-500 border border-zinc-200">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            Safety Check: Manual Review Required
          </span>
          <span className="text-xs text-zinc-400">
            Automated safety check APIs not yet available (UKA-X01.08).
          </span>
        </div>
      </div>

      {/* ── Content body ──────────────────────────────────────────────── */}
      <div className="px-5 py-5">
        <ContentBodyErrorBoundary>
          <ContentBodyRenderer body={item.body} />
        </ContentBodyErrorBoundary>
      </div>

      {/* ── Verdict section ───────────────────────────────────────────── */}
      <div className="px-5 pb-5 border-t border-zinc-100 pt-5">
        <p className="text-sm font-semibold text-[#18181B] mb-3">Verdict</p>

        {/* Verdict buttons (4) */}
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 mb-4">
          {VERDICT_BUTTONS.map(btn => {
            const isApprove = btn.action === 'APPROVE';
            const isSelected = selectedAction === btn.action;
            const isDisabled =
              hashMismatch ||
              (isApprove && !approveUnlocked);

            return (
              <button
                key={btn.action}
                type="button"
                disabled={isDisabled}
                onClick={() => handleActionSelect(btn.action)}
                aria-pressed={isSelected}
                title={
                  isApprove && !approveUnlocked
                    ? 'Please review the content before approving (2-second minimum)'
                    : undefined
                }
                className={[
                  'relative px-3 py-2 rounded text-sm font-medium border transition-all duration-150',
                  'focus:outline-none focus:ring-2 focus:ring-offset-1',
                  btn.ring,
                  isDisabled
                    ? 'opacity-40 cursor-not-allowed bg-zinc-100 text-zinc-400 border-zinc-200'
                    : isSelected
                    ? `${btn.bg} ${btn.text} ${btn.border} ring-2 ring-offset-1`
                    : `bg-white ${btn.text.replace('white', 'zinc-700')} ${btn.border} ${btn.bgHover} hover:text-white`,
                  !isDisabled && !isSelected
                    ? 'text-zinc-700 hover:text-white'
                    : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {isApprove && !approveUnlocked && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-zinc-300" />
                )}
                {btn.label}
              </button>
            );
          })}
        </div>

        {/* Approve timer notice */}
        {!approveUnlocked && (
          <p className="text-xs text-zinc-400 mb-3">
            Approve will unlock after a brief review period.
          </p>
        )}

        {/* Comment field — optional unless action is REJECT or CHANGE_REQUESTED */}
        <div className="mb-3">
          <label htmlFor={`comment-${contentId}`} className="block text-sm font-medium text-[#18181B] mb-1">
            Comment
            {commentRequired && (
              <span className="ml-1 text-rose-600">* required (min 10 characters)</span>
            )}
            {!commentRequired && (
              <span className="ml-1 text-zinc-400 font-normal">optional</span>
            )}
          </label>
          <textarea
            id={`comment-${contentId}`}
            rows={3}
            value={comment}
            onChange={e => setComment(e.target.value)}
            placeholder={
              commentRequired
                ? 'Provide your reason for this verdict (required).'
                : 'Add any notes or context for this verdict.'
            }
            className="w-full px-3 py-2 text-sm text-[#18181B] bg-white border border-zinc-300 rounded resize-y focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent placeholder:text-zinc-400"
          />
          {commentRequired && comment.trim().length > 0 && comment.trim().length < 10 && (
            <p className="text-xs text-rose-600 mt-1">
              {10 - comment.trim().length} more characters required.
            </p>
          )}
        </div>

        {/* Clinical justification — required for offLabel/controversial */}
        {requiresJustification && (
          <div className="mb-4">
            <label htmlFor={`justification-${contentId}`} className="block text-sm font-medium text-[#18181B] mb-1">
              Clinical Justification
              <span className="ml-1 text-rose-600">* required</span>
            </label>
            <p className="text-xs text-zinc-500 mb-1.5">
              This item is off-label or clinically controversial. Provide your explicit clinical
              rationale for the verdict — this becomes the compliance artifact for this approval.
            </p>
            <textarea
              id={`justification-${contentId}`}
              rows={4}
              value={justification}
              onChange={e => setJustification(e.target.value)}
              placeholder="Describe the clinical basis for your verdict on this off-label or controversial item."
              className="w-full px-3 py-2 text-sm text-[#18181B] bg-white border border-amber-300 rounded resize-y focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent placeholder:text-zinc-400"
            />
            {justification.trim().length === 0 && (
              <p className="text-xs text-amber-700 mt-1">
                Justification required before submission.
              </p>
            )}
          </div>
        )}

        {/* Server error */}
        {error && (
          <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Submit button */}
        <button
          type="submit"
          disabled={!canSubmit}
          className={[
            'w-full sm:w-auto px-6 py-2.5 rounded text-sm font-semibold transition-all duration-150',
            'focus:outline-none focus:ring-2 focus:ring-rose-500 focus:ring-offset-1',
            canSubmit
              ? 'bg-[#E11D48] text-white hover:bg-rose-700 cursor-pointer'
              : 'bg-zinc-100 text-zinc-400 cursor-not-allowed',
          ].join(' ')}
        >
          {submitting ? 'Submitting...' : 'Submit Verdict'}
        </button>

        {!selectedAction && (
          <p className="text-xs text-zinc-400 mt-2">Select a verdict above to continue.</p>
        )}
      </div>

      {/* ── Approve All Supplements (formulary_supplement sections only) ── */}
      {item.type === 'formulary_supplement' && groupItems && groupItems.length > 0 && (
        <div className="px-5 pb-5 border-t border-zinc-100 pt-4">
          <p className="text-xs text-zinc-500 mb-2">
            Batch action — applies to all IN_REVIEW items in this supplement group:
          </p>
          <ApproveAllButton
            items={groupItems}
            tenantId={tenantId}
          />
        </div>
      )}
    </form>
  );
}

// ── Helper functions ──────────────────────────────────────────────────────────

function getTierLabel(tier: string | null): string | null {
  if (tier === 'supplement') return 'T1 — Supplement';
  if (tier === 'rx') return 'T2 — Prescription';
  if (tier === 'longevity') return 'T3 — Longevity';
  return null;
}

function getTypeLabel(type: ClinicalReviewItem['type']): string {
  const labels: Record<ClinicalReviewItem['type'], string> = {
    formulary_supplement: 'Formulary · Supplement',
    formulary_rx: 'Formulary · Rx',
    formulary_longevity: 'Formulary · Longevity',
    biomarker_range: 'Biomarker Range',
    evidence_brief: 'Evidence Brief',
    range_decision: 'Range Decision',
    gap_question: 'Gap Question',
    methodology: 'Methodology',
    attestation: 'Master Attestation',
    protocol_rule: 'Protocol Rule',
  };
  return labels[type] ?? type;
}

function getActionLabel(action: VerdictAction): string {
  const labels: Record<VerdictAction, string> = {
    APPROVE: 'Approved',
    APPROVE_WITH_CONDITIONS: 'Approved With Conditions',
    CHANGE_REQUESTED: 'Change Requested',
    REJECT: 'Rejected',
    WITHDRAW: 'Withdrawn',
    REAFFIRM: 'Reaffirmed',
    OVERRIDE_SAFETY_CHECK: 'Safety Check Override',
  };
  return labels[action] ?? action;
}

function getStatusClasses(status: ClinicalReviewItem['status']): string {
  switch (status) {
    case 'APPROVED':
    case 'APPROVED_WITH_CONDITIONS':
      return 'bg-green-50 text-green-700 border-green-200';
    case 'REJECTED':
      return 'bg-red-50 text-red-700 border-red-200';
    case 'CHANGE_REQUESTED':
      return 'bg-orange-50 text-orange-700 border-orange-200';
    case 'IN_REVIEW':
      return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'WITHDRAWN':
    case 'BLOCKED':
      return 'bg-zinc-100 text-zinc-600 border-zinc-200';
    default:
      return 'bg-zinc-50 text-zinc-500 border-zinc-200';
  }
}

// ── Flat Body Display (fallback renderer) ────────────────────────────────────
// Renders any body object as a clean labeled field list.
// Used when the typed renderer crashes (field name mismatch between seed schema
// and TypeScript interface). All clinical data is still readable.

const SKIP_FIELDS = new Set(['contentTypeId']);

function formatLabel(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, s => s.toUpperCase())
    .trim();
}

function renderValue(val: unknown): React.ReactNode {
  if (val === null || val === undefined) return <span className="text-zinc-400">—</span>;
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  if (typeof val === 'number') return String(val);
  if (typeof val === 'string') return val;
  if (Array.isArray(val)) {
    if (val.length === 0) return <span className="text-zinc-400">None</span>;
    if (typeof val[0] === 'string') {
      return (
        <ul className="list-disc list-inside space-y-0.5">
          {(val as string[]).map((s, i) => <li key={i} className="text-sm text-zinc-700">{s}</li>)}
        </ul>
      );
    }
    // Array of objects (e.g. evidenceReferences)
    return (
      <div className="space-y-2">
        {(val as Record<string, unknown>[]).map((obj, i) => (
          <div key={i} className="bg-zinc-50 border border-zinc-200 rounded px-3 py-2 text-sm">
            {Object.entries(obj).map(([k, v]) => (
              <span key={k} className="inline-block mr-3 text-zinc-700">
                <span className="text-zinc-400 text-xs">{formatLabel(k)}: </span>
                {String(v ?? '—')}
              </span>
            ))}
          </div>
        ))}
      </div>
    );
  }
  // Nested object
  if (typeof val === 'object') {
    return (
      <div className="space-y-1 pl-3 border-l-2 border-zinc-200">
        {Object.entries(val as Record<string, unknown>).map(([k, v]) => (
          <div key={k} className="text-sm">
            <span className="text-zinc-500 text-xs font-medium">{formatLabel(k)}: </span>
            <span className="text-zinc-700">{String(v ?? '—')}</span>
          </div>
        ))}
      </div>
    );
  }
  return String(val);
}

function FlatBodyDisplay({ body }: { body: Record<string, unknown> | null }): React.ReactElement {
  if (!body) {
    return <p className="text-sm text-zinc-500">No content available.</p>;
  }
  const entries = Object.entries(body).filter(([k]) => !SKIP_FIELDS.has(k));
  return (
    <div className="space-y-4">
      {entries.map(([key, val]) => (
        <div key={key}>
          <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-1">
            {formatLabel(key)}
          </p>
          <div className="text-sm text-zinc-800">{renderValue(val)}</div>
        </div>
      ))}
    </div>
  );
}

// ── Content Body Error Boundary ───────────────────────────────────────────────

interface ContentBodyErrorBoundaryState { hasError: boolean; }

class ContentBodyErrorBoundary extends React.Component<
  { children: React.ReactNode },
  ContentBodyErrorBoundaryState
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(): ContentBodyErrorBoundaryState {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      const body = (this.props.children as React.ReactElement)?.props?.body as Record<string, unknown> | null;
      return (
        <FlatBodyDisplay body={body} />
      );
    }
    return this.props.children as React.ReactElement;
  }
}

// ── Content Body Renderer ─────────────────────────────────────────────────────

function ContentBodyRenderer({ body }: { body: ReviewItemBody }): React.ReactElement {
  if (isFormularySupplementBody(body)) {
    return (
      <div className="space-y-4">
        <FieldRow label="Product ID" value={body.productId} mono />
        <FieldRow label="Indication" value={body.indication} />
        <FieldRow label="Dosing" value={body.dosing} />
        <EvidenceLevelBadge level={body.evidenceLevel} />
        <FieldRow label="Evidence Summary" value={body.evidenceSummary} />
        {body.contraindications.length > 0 && (
          <BulletList label="Contraindications" items={body.contraindications} color="red" />
        )}
        {body.safetyRules.length > 0 && <SafetyRulesList rules={body.safetyRules} />}
        {body.evidenceReferences.length > 0 && (
          <EvidenceReferencesList refs={body.evidenceReferences} />
        )}
        {body.targetBiomarkers.length > 0 && (
          <TagList label="Target Biomarkers" tags={body.targetBiomarkers} />
        )}
      </div>
    );
  }

  if (isFormularyRxBody(body)) {
    return (
      <div className="space-y-4">
        <FieldRow label="Product ID" value={body.productId} mono />
        <FieldRow label="FDA Indication" value={body.fdaIndication} />
        {body.offLabelUse && (
          <div className="bg-amber-50 border border-amber-200 rounded p-3">
            <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-1">
              Off-Label Use
            </p>
            <p className="text-sm text-amber-900">{body.offLabelUse}</p>
          </div>
        )}
        <FieldRow label="Dosing" value={body.dosing} />
        {body.pregnancyCategory && (
          <FieldRow label="Pregnancy Category" value={body.pregnancyCategory} />
        )}
        {body.scheduleClassification && (
          <FieldRow label="DEA Schedule" value={body.scheduleClassification} />
        )}
        <FieldRow
          label="Compounding Required"
          value={body.compoundingRequired ? 'Yes' : 'No'}
        />
        {body.monitoringRequirements.length > 0 && (
          <BulletList
            label="Monitoring Requirements"
            items={body.monitoringRequirements}
            color="blue"
          />
        )}
        {body.contraindications.length > 0 && (
          <BulletList label="Contraindications" items={body.contraindications} color="red" />
        )}
        {body.drugInteractions.length > 0 && (
          <DrugInteractionsList interactions={body.drugInteractions} />
        )}
        {body.safetyRules.length > 0 && <SafetyRulesList rules={body.safetyRules} />}
        <FieldRow label="Evidence Summary" value={body.evidenceSummary} />
        {body.evidenceReferences.length > 0 && (
          <EvidenceReferencesList refs={body.evidenceReferences} />
        )}
      </div>
    );
  }

  if (isFormularyLongevityBody(body)) {
    return (
      <div className="space-y-4">
        <FieldRow label="Product ID" value={body.productId} mono />
        <FieldRow label="FDA Status" value={body.fdaStatus.replace(/-/g, ' ')} />
        <div className="bg-amber-50 border border-amber-200 rounded p-3">
          <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-1">
            Off-Label Use
          </p>
          <p className="text-sm text-amber-900">{body.offLabelUse}</p>
        </div>
        <FieldRow label="Evidence Level" value={body.evidenceLevel.replace(/-/g, ' ')} />
        <RiskProfileBadge profile={body.riskProfile} />
        <FieldRow label="xPrize Relevance" value={body.xPrizeRelevance} />
        {body.compoundingRequired && (
          <FieldRow
            label="Compounding Notes"
            value={body.compoundingNotes ?? 'Compounding required — no additional notes.'}
          />
        )}
        {body.withinScopeOfCCMedicalPC !== null && (
          <FieldRow
            label="Within Scope of CC Medical PC"
            value={
              body.withinScopeOfCCMedicalPC ? 'Yes — confirm on approval' : 'No / Not confirmed'
            }
          />
        )}
        {body.monitoringRequirements.length > 0 && (
          <BulletList
            label="Monitoring Requirements"
            items={body.monitoringRequirements}
            color="blue"
          />
        )}
        {body.contraindications.length > 0 && (
          <BulletList label="Contraindications" items={body.contraindications} color="red" />
        )}
        {body.safetyRules.length > 0 && <SafetyRulesList rules={body.safetyRules} />}
        {body.evidenceReferences.length > 0 && (
          <EvidenceReferencesList refs={body.evidenceReferences} />
        )}
      </div>
    );
  }

  if (isBiomarkerRangeBody(body)) {
    return (
      <div className="space-y-4">
        <div className="flex items-baseline gap-2">
          <span className="text-lg font-semibold text-[#18181B]">{body.biomarkerName}</span>
          <span className="text-sm font-mono text-zinc-500">{body.abbreviation}</span>
          {body.controversialFlag && (
            <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
              Controversial
            </span>
          )}
        </div>
        {body.controversialRationale && (
          <div className="bg-amber-50 border border-amber-200 rounded p-3">
            <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-1">
              Controversial — Rationale
            </p>
            <p className="text-sm text-amber-900">{body.controversialRationale}</p>
          </div>
        )}
        {/* Three-tier range table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-zinc-50">
                <th className="text-left px-3 py-2 text-xs font-semibold text-zinc-500 uppercase tracking-wide border border-zinc-200">
                  Tier
                </th>
                <th className="text-right px-3 py-2 text-xs font-semibold text-zinc-500 uppercase tracking-wide border border-zinc-200">
                  Min
                </th>
                <th className="text-right px-3 py-2 text-xs font-semibold text-zinc-500 uppercase tracking-wide border border-zinc-200">
                  Max
                </th>
                <th className="text-left px-3 py-2 text-xs font-semibold text-zinc-500 uppercase tracking-wide border border-zinc-200">
                  Unit
                </th>
                <th className="text-left px-3 py-2 text-xs font-semibold text-zinc-500 uppercase tracking-wide border border-zinc-200">
                  Notes
                </th>
              </tr>
            </thead>
            <tbody>
              {[
                { label: 'Standard', data: body.standardRange, className: '' },
                { label: 'Functional', data: body.functionalRange, className: 'bg-blue-50' },
                { label: 'xPrize', data: body.xPrizeRange, className: 'bg-purple-50' },
              ].map(row => (
                <tr key={row.label} className={row.className}>
                  <td className="px-3 py-2 font-medium text-[#18181B] border border-zinc-200">
                    {row.label}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-[#18181B] border border-zinc-200">
                    {row.data.min}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-[#18181B] border border-zinc-200">
                    {row.data.max}
                  </td>
                  <td className="px-3 py-2 text-zinc-600 border border-zinc-200">
                    {row.data.unit}
                  </td>
                  <td className="px-3 py-2 text-zinc-500 text-xs border border-zinc-200">
                    {row.data.notes ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {body.genderSpecific && body.genderSpecificNotes && (
          <FieldRow label="Gender-Specific Notes" value={body.genderSpecificNotes} />
        )}
        {body.ageRangeApplicability && (
          <FieldRow label="Age Range Applicability" value={body.ageRangeApplicability} />
        )}
        <FieldRow label="Source Hierarchy" value={body.sourceHierarchy} />
        <FieldRow
          label="Sanity Check"
          value={body.rangeSanityCheckPassed ? 'Passed' : 'Failed'}
        />
      </div>
    );
  }

  if (isEvidenceBriefBody(body)) {
    return (
      <div className="space-y-4">
        <FieldRow label="Brief ID" value={body.briefId} mono />
        <FieldRow label="Origin / Provenance" value={body.origin} />
        <div className="flex flex-wrap gap-2">
          <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getRiskTierClasses(body.riskTier)}`}>
            Risk: {body.riskTier.replace(/-/g, ' ')}
          </span>
          <span className="px-2 py-0.5 rounded text-xs font-medium bg-zinc-50 text-zinc-600 border border-zinc-200">
            FDA: {body.fdaStatus.replace(/-/g, ' ')}
          </span>
          {body.scheduleClassification && (
            <span className="px-2 py-0.5 rounded text-xs font-medium bg-zinc-50 text-zinc-600 border border-zinc-200">
              {body.scheduleClassification}
            </span>
          )}
          <span className={`px-2 py-0.5 rounded text-xs font-medium border ${body.humanTrialData ? 'bg-green-50 text-green-700 border-green-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
            {body.humanTrialData ? 'Human trial data available' : 'Animal data only'}
          </span>
          {body.oncogenicPathwayConcern && (
            <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-50 text-red-700 border border-red-200">
              Oncogenic pathway concern
            </span>
          )}
        </div>
        <div>
          <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">
            Evidence For
          </p>
          <EvidenceReferencesList refs={body.evidenceFor} />
        </div>
        <div>
          <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">
            Honest Counterarguments
          </p>
          <ul className="space-y-1.5">
            {body.honestCounterarguments.map((arg, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-zinc-700">
                <span className="text-zinc-400 mt-0.5 flex-shrink-0">—</span>
                <span>{arg}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded p-3">
          <p className="text-xs font-semibold text-blue-800 uppercase tracking-wide mb-1">
            Net Clinical Position (Platform)
          </p>
          <p className="text-sm text-blue-900">{body.netClinicalPosition}</p>
        </div>
        <div className="bg-zinc-50 border border-zinc-200 rounded p-3">
          <p className="text-xs font-semibold text-zinc-600 uppercase tracking-wide mb-1">
            Annotation Prompt
          </p>
          <p className="text-sm text-zinc-700">{body.annotationPrompt}</p>
        </div>
      </div>
    );
  }

  if (isRangeDecisionBody(body)) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-blue-50 border border-blue-200 rounded p-3">
            <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-1">
              Platform Value
            </p>
            <p className="text-xl font-mono font-bold text-blue-900">
              {body.platformValue} <span className="text-sm font-sans font-normal">{body.unit}</span>
            </p>
          </div>
          <div className="bg-zinc-50 border border-zinc-200 rounded p-3">
            <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-1">
              Guideline ({body.guidelineSource})
            </p>
            <p className="text-xl font-mono font-bold text-zinc-700">
              {body.guidelineValue} <span className="text-sm font-sans font-normal">{body.unit}</span>
            </p>
          </div>
        </div>
        <FieldRow label="Deviation Rationale" value={body.deviationRationale} />
        <div className="bg-red-50 border border-red-200 rounded p-3">
          <p className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-1">
            Risk of Deviation
          </p>
          <p className="text-sm text-red-900">{body.riskOfDeviation}</p>
        </div>
        {body.supportingEvidence.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">
              Supporting Evidence
            </p>
            <EvidenceReferencesList refs={body.supportingEvidence} />
          </div>
        )}
        {body.counterEvidence.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">
              Counter Evidence
            </p>
            <EvidenceReferencesList refs={body.counterEvidence} />
          </div>
        )}
      </div>
    );
  }

  if (isGapQuestionBody(body)) {
    return (
      <div className="space-y-4">
        <FieldRow label="Gap ID" value={body.gapId} mono />
        <div className="bg-blue-50 border border-blue-200 rounded p-3">
          <p className="text-xs font-semibold text-blue-800 uppercase tracking-wide mb-1">
            Clinical Question
          </p>
          <p className="text-sm font-medium text-blue-900">{body.question}</p>
        </div>
        <FieldRow label="Clinical Context" value={body.clinicalContext} />
        <FieldRow label="Current Platform Behavior" value={body.currentPlatformBehavior} />
        <FieldRow label="Downstream Impact" value={body.downstreamImpact} />
        {body.answerOptions && body.answerOptions.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">
              Answer Options
            </p>
            <ul className="space-y-1">
              {body.answerOptions.map((opt, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-zinc-700">
                  <span className="w-5 h-5 rounded-full bg-zinc-100 border border-zinc-300 text-xs flex items-center justify-center font-mono text-zinc-500">
                    {String.fromCharCode(65 + i)}
                  </span>
                  {opt}
                </li>
              ))}
            </ul>
          </div>
        )}
        <p className="text-xs text-zinc-500 italic">
          Your answer goes in the Comment field below. Use the verdict to indicate your response type.
        </p>
      </div>
    );
  }

  if (isMethodologyBody(body)) {
    return (
      <div className="space-y-4">
        <FieldRow label="Methodology Version" value={body.methodologyVersion} mono />
        <FieldRow label="Range System Description" value={body.rangeSystemDescription} />
        <FieldRow label="Data Validation Process" value={body.dataValidationProcess} />
        <FieldRow label="Conflict Resolution Process" value={body.conflictResolutionProcess} />
        {[
          { label: 'Standard Range Sources', items: body.standardRangeSourceHierarchy },
          { label: 'Functional Range Sources', items: body.functionalRangeSourceHierarchy },
          { label: 'xPrize Range Sources', items: body.xPrizeRangeSourceHierarchy },
        ].map(({ label, items }) => (
          <BulletList key={label} label={label} items={items} color="zinc" />
        ))}
        {body.knownLimitations.length > 0 && (
          <BulletList label="Known Limitations" items={body.knownLimitations} color="amber" />
        )}
      </div>
    );
  }

  if (isAttestationBody(body)) {
    return (
      <div className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded p-4">
          <p className="text-sm text-blue-900 leading-relaxed">{body.attestationText}</p>
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <FieldRow label="Reviewer" value={body.reviewerName} />
          <FieldRow label="Credentials" value={body.reviewerCredentials} />
          <FieldRow label="Title" value={body.reviewerTitle} />
          <FieldRow label="Tenant" value={body.tenantName} />
          <FieldRow label="Review Cycle ID" value={body.reviewCycleId} mono />
          <FieldRow
            label="Total Sections"
            value={String(body.totalSectionsForReview)}
          />
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded p-3">
          <p className="text-xs text-amber-800">
            This attestation is only available after all {body.totalSectionsForReview} sections are reviewed.
            Approval locks the entire review instance.
          </p>
        </div>
      </div>
    );
  }

  if (isProtocolRuleBody(body)) {
    return (
      <div className="space-y-4">
        <FieldRow label="Rule ID" value={body.ruleId} mono />
        <div className="bg-blue-50 border border-blue-200 rounded p-3">
          <p className="text-xs font-semibold text-blue-800 uppercase tracking-wide mb-1">
            Trigger
          </p>
          <p className="text-sm text-blue-900">{body.trigger}</p>
        </div>
        <div className="bg-green-50 border border-green-200 rounded p-3">
          <p className="text-xs font-semibold text-green-800 uppercase tracking-wide mb-1">
            Action
          </p>
          <p className="text-sm text-green-900">{body.action}</p>
        </div>
        <FieldRow label="Rationale" value={body.rationale} />
        {body.contraindications.length > 0 && (
          <BulletList label="Contraindications" items={body.contraindications} color="red" />
        )}
        {body.evidenceReferences.length > 0 && (
          <EvidenceReferencesList refs={body.evidenceReferences} />
        )}
      </div>
    );
  }

  // Fallback for unknown type
  return (
    <div className="bg-zinc-50 border border-zinc-200 rounded p-4">
      <p className="text-xs font-mono text-zinc-500">Unknown content type. Raw body:</p>
      <pre className="mt-2 text-xs text-zinc-600 overflow-auto font-mono whitespace-pre-wrap">
        {JSON.stringify(body, null, 2)}
      </pre>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function FieldRow({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): React.ReactElement {
  return (
    <div>
      <dt className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-0.5">
        {label}
      </dt>
      <dd className={`text-sm text-[#18181B] leading-relaxed ${mono ? 'font-mono' : ''}`}>
        {value}
      </dd>
    </div>
  );
}

function BulletList({
  label,
  items,
  color,
}: {
  label: string;
  items: string[];
  color: 'red' | 'blue' | 'amber' | 'zinc';
}): React.ReactElement {
  const dotColor = {
    red: 'bg-red-400',
    blue: 'bg-blue-400',
    amber: 'bg-amber-400',
    zinc: 'bg-zinc-400',
  }[color];

  return (
    <div>
      <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-1.5">
        {label}
      </p>
      <ul className="space-y-1">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-zinc-700">
            <span className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${dotColor}`} />
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

import type { SafetyRule } from '../../data/portal/contentTypes';

function SafetyRulesList({ rules }: { rules: SafetyRule[] }): React.ReactElement {
  return (
    <div>
      <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-1.5">
        Safety Rules
      </p>
      <div className="space-y-1.5">
        {rules.map((rule, i) => (
          <div key={i} className={`flex items-start gap-2 text-sm px-3 py-2 rounded border ${
            rule.severity === 'CONTRAINDICATION'
              ? 'bg-red-50 border-red-200'
              : rule.severity === 'WARNING'
              ? 'bg-amber-50 border-amber-200'
              : 'bg-blue-50 border-blue-200'
          }`}>
            <span className={`text-xs font-bold uppercase mt-0.5 flex-shrink-0 ${
              rule.severity === 'CONTRAINDICATION'
                ? 'text-red-700'
                : rule.severity === 'WARNING'
                ? 'text-amber-700'
                : 'text-blue-700'
            }`}>
              {rule.severity}
            </span>
            <div>
              <p className="text-[#18181B]">{rule.rule}</p>
              {rule.populations.length > 0 && (
                <p className="text-xs text-zinc-500 mt-0.5">
                  Populations: {rule.populations.join(', ')}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

import type { EvidenceReference, DrugInteraction } from '../../data/portal/contentTypes';

function EvidenceReferencesList({
  refs,
}: {
  refs: EvidenceReference[];
}): React.ReactElement {
  const levelColors: Record<EvidenceReference['evidenceLevel'], string> = {
    'RCT': 'bg-green-50 text-green-700 border-green-200',
    'meta-analysis': 'bg-green-50 text-green-700 border-green-200',
    'cohort': 'bg-blue-50 text-blue-700 border-blue-200',
    'case-series': 'bg-amber-50 text-amber-700 border-amber-200',
    'animal': 'bg-orange-50 text-orange-700 border-orange-200',
    'expert-opinion': 'bg-zinc-100 text-zinc-600 border-zinc-200',
  };

  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">
        Evidence References
      </p>
      {refs.map((ref, i) => (
        <div key={i} className="border border-zinc-200 rounded p-3 bg-zinc-50">
          <div className="flex items-start justify-between gap-2 mb-1">
            <p className="text-sm text-zinc-700 leading-snug">{ref.citation}</p>
            <span className={`flex-shrink-0 px-1.5 py-0.5 rounded text-xs font-medium border ${levelColors[ref.evidenceLevel]}`}>
              {ref.evidenceLevel.replace(/-/g, ' ')}
            </span>
          </div>
          {ref.pmid && (
            <p className="text-xs font-mono text-zinc-400">PMID: {ref.pmid}</p>
          )}
          {ref.notes && <p className="text-xs text-zinc-500 mt-0.5">{ref.notes}</p>}
        </div>
      ))}
    </div>
  );
}

function DrugInteractionsList({
  interactions,
}: {
  interactions: DrugInteraction[];
}): React.ReactElement {
  return (
    <div>
      <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-1.5">
        Drug Interactions
      </p>
      <div className="space-y-1.5">
        {interactions.map((int, i) => (
          <div key={i} className={`px-3 py-2 rounded border text-sm ${
            int.clinicalSignificance === 'MAJOR'
              ? 'bg-red-50 border-red-200'
              : int.clinicalSignificance === 'MODERATE'
              ? 'bg-amber-50 border-amber-200'
              : 'bg-zinc-50 border-zinc-200'
          }`}>
            <div className="flex items-center gap-2">
              <span className="font-medium text-[#18181B]">{int.drug}</span>
              <span className={`text-xs font-bold uppercase ${
                int.clinicalSignificance === 'MAJOR'
                  ? 'text-red-700'
                  : int.clinicalSignificance === 'MODERATE'
                  ? 'text-amber-700'
                  : 'text-zinc-500'
              }`}>
                {int.clinicalSignificance}
              </span>
            </div>
            <p className="text-zinc-600 mt-0.5">{int.mechanism}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function TagList({ label, tags }: { label: string; tags: string[] }): React.ReactElement {
  return (
    <div>
      <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-1.5">
        {label}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {tags.map((tag, i) => (
          <span
            key={i}
            className="px-2 py-0.5 rounded text-xs font-mono text-zinc-600 bg-zinc-100 border border-zinc-200"
          >
            {tag}
          </span>
        ))}
      </div>
    </div>
  );
}

function EvidenceLevelBadge({
  level,
}: {
  level: 'strong' | 'moderate' | 'weak' | 'insufficient';
}): React.ReactElement {
  const classes = {
    strong: 'bg-green-50 text-green-700 border-green-200',
    moderate: 'bg-blue-50 text-blue-700 border-blue-200',
    weak: 'bg-amber-50 text-amber-700 border-amber-200',
    insufficient: 'bg-red-50 text-red-700 border-red-200',
  }[level];

  return (
    <div>
      <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-1">
        Evidence Level
      </p>
      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium border ${classes}`}>
        {level.charAt(0).toUpperCase() + level.slice(1)}
      </span>
    </div>
  );
}

function RiskProfileBadge({
  profile,
}: {
  profile: 'low' | 'moderate' | 'high' | 'investigational';
}): React.ReactElement {
  const classes = {
    low: 'bg-green-50 text-green-700 border-green-200',
    moderate: 'bg-amber-50 text-amber-700 border-amber-200',
    high: 'bg-red-50 text-red-700 border-red-200',
    investigational: 'bg-purple-50 text-purple-700 border-purple-200',
  }[profile];

  return (
    <div>
      <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-1">
        Risk Profile
      </p>
      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium border ${classes}`}>
        {profile.charAt(0).toUpperCase() + profile.slice(1)}
      </span>
    </div>
  );
}

function getRiskTierClasses(tier: string): string {
  switch (tier) {
    case 'standard':
      return 'bg-green-50 text-green-700 border-green-200';
    case 'elevated':
      return 'bg-amber-50 text-amber-700 border-amber-200';
    case 'high':
      return 'bg-red-50 text-red-700 border-red-200';
    case 'investigational-only':
      return 'bg-purple-50 text-purple-700 border-purple-200';
    default:
      return 'bg-zinc-50 text-zinc-600 border-zinc-200';
  }
}
